import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext(null);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const showToast = useCallback((message, type = 'info', duration = 3000) => {
        const id = Date.now() + Math.random();
        const toast = { id, message, type, duration };

        setToasts(prev => [...prev, toast]);

        if (duration > 0) {
            setTimeout(() => {
                setToasts(prev => prev.filter(t => t.id !== id));
            }, duration);
        }

        return id;
    }, []);

    const dismissToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const value = {
        showToast,
        dismissToast,
        toasts,
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastContainer toasts={toasts} onDismiss={dismissToast} />
        </ToastContext.Provider>
    );
};

const ToastContainer = ({ toasts, onDismiss }) => {
    if (toasts.length === 0) return null;

    return (
        <div
            style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 10000,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                maxWidth: '400px',
            }}
        >
            {toasts.map(toast => (
                <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
            ))}
        </div>
    );
};

const Toast = ({ toast, onDismiss }) => {
    const typeStyles = {
        success: {
            background: '#10B981',
            color: '#fff',
            border: '4px solid #000',
        },
        error: {
            background: '#EF4444',
            color: '#fff',
            border: '4px solid #000',
        },
        warning: {
            background: '#F59E0B',
            color: '#fff',
            border: '4px solid #000',
        },
        info: {
            background: '#3B82F6',
            color: '#fff',
            border: '4px solid #000',
        },
    };

    const style = typeStyles[toast.type] || typeStyles.info;

    return (
        <div
            style={{
                ...style,
                padding: '16px 20px',
                boxShadow: '6px 6px 0 #000',
                fontWeight: '600',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                animation: 'slideIn 0.3s ease-out',
                minWidth: '300px',
            }}
        >
            <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
            <span>{toast.message}</span>
            <button
                onClick={() => onDismiss(toast.id)}
                style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    padding: '0',
                    width: '24px',
                    height: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                ×
            </button>
        </div>
    );
};

export default ToastContext;
